/**
 * The quotes of a company marker's attributes, made straight.
 *
 * This file is consumed by `@shaarp/ui`. `@shaarp/api` cannot import a runtime
 * value from this package (its barrel re-exports a directory, which Node
 * refuses to resolve from the API's CommonJS build), so it keeps a mirrored
 * copy in `api/src/common/utils/company-mention-markers.ts`, held to this one
 * by `api/src/common/utils/company-marker-quotes-parity.spec.ts`. Keep both in
 * sync: the browser and the server must read one marker as one company.
 */

/** Curly and prime quotes a model may write around an attribute value. */
const TYPOGRAPHIC_QUOTES = "“”„‟″‶";

/** A typographic quote opening a value: right after `attribute=`. */
const OPENING_QUOTE = new RegExp(`(\\b[a-z]+=)[${TYPOGRAPHIC_QUOTES}]`, "g");

/**
 * A typographic quote closing a value: followed by the next `attribute=`, or
 * by the end of the marker.
 */
const CLOSING_QUOTE = new RegExp(
  `[${TYPOGRAPHIC_QUOTES}](?=\\s+[a-z]+=|\\s*$)`,
  "g",
);

/**
 * `attributes` — the inside of one `[@shaarp/company …]` marker — with the
 * typographic quotes that delimit a value turned into `"`, so that a model
 * writing `name=“Acme”` is read as `name="Acme"`.
 *
 * Only a delimiter is turned, never a quote inside a value: a brief citing a
 * ranking — `brief="Classée « meilleure » du coin"`, or with “ ” — keeps its
 * words instead of ending at the first one. French guillemets never delimit a
 * value, so they are never turned.
 */
export function normalizeMarkerQuotes(attributes: string): string {
  return attributes.replace(OPENING_QUOTE, '$1"').replace(CLOSING_QUOTE, '"');
}
